import type { DocumentType } from '../../components/views/document-view/document-viewer.ts';
import type { Page } from '../../components/views/settings-view/types.ts';
import type { Route } from '../../routes.ts';
import type { ImageQueuePatientStatus } from '../../types/Image.d.ts';
import type { Config, Settings, SystemSettings } from '../../types/Settings.d.ts';
import type { UserInfo } from '../../types/UserInfo.d.ts';
import { createSelector } from '../createSelector.ts';
import { getStore } from '../store/getStore.ts';
import type { ReduxState } from '../types.ts';

export const verifyDisabled = createSelector<boolean>('app.verifyDisabled');

export const pageSelector = createSelector<Route>('app.page');

/** Returns the organization slug from the route. */
export const organizationSelector = createSelector<string>('app.params.organization');
export const patientIdSelector = createSelector<string>(['app.patientId', 'app.patient.id', 'app.params.patient']);
export const patientSerSelector = createSelector<string | number, int>(
  ['app.params.patientSer', 'app.patient.ser', 'app.params.ser'],
  undefined,
  v => parseInt(`${v}`, 10)
);

export const configSelector = createSelector<Config>('app.config');
export const meSelector = createSelector<UserInfo>('app.me');
export const settingsSelector = createSelector<Settings>('app.settings');
export const imageQueuePatientStatusSelector = createSelector<ImageQueuePatientStatus>('app.imageQueuePatientStatus');
export const settingsLoadedSelector = createSelector<boolean>('app.settingsLoaded');

export const documentIdSelector = createSelector<string>('app.params.documentId');
export const documentTypeSelector = createSelector<DocumentType>('app.params.documentType');

/** The Page['name'] of the current settings page. */
export const settingsPageSelector = createSelector<Page['name']>('app.params.page', 'main');

export const showOverlaySelector = createSelector<boolean>('app.showOverlay', false);

export const systemSettingsSelector = createSelector<SystemSettings>('app.systemSettings');

export const authGrantedSelector = createSelector<boolean>('app.authGranted', undefined);
export const authReadySelector = createSelector<boolean>('app.authReady', false);

export function paramSelector(param: string, defaultValue?: string): (state?: ReduxState) => string | undefined {
  return createSelector<string>(`app.params.${param}`, defaultValue);
}

/** Gets the most applicable organization ID basesd on the current state. */
export function organizationIdSelector(state: ReduxState = getStore().getState()): string | undefined {
  const me = meSelector(state);
  const { id } = ((me || { organizations: [] })
    .organizations.find(({ slug }) => organizationSelector(state) === slug) || {});

  if (id) return id;

  if (me) return me.organizations[0].id;
}

/** Gets the most applicable organization slug based on the current state. */
export function organizationSlugSelector(state: ReduxState = getStore().getState()): string | undefined {
  const me = meSelector(state);
  const { slug } = ((me || { organizations: [] })
    .organizations.find(({ slug }) => organizationSelector(state) === slug) || {});

  if (slug) return slug;

  if (me) return me.organizations[0].slug;
}